import {
  AvatarIcon,
  Button,
  ButtonLink,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorListItem,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TransactionIcon,
} from '@razz-team/ui-web';
import { ApiVault } from '../../../openapi';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect } from 'react';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';
import { useVaultTerminateMutation } from '../../../api/vaults';
import { isMoneyZero } from '@razz-team/utils';

export function TerminateVaultDialog({
  open,
  onClose,
  vault,
}: {
  open: boolean;
  onClose: () => void;
  vault: ApiVault;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const idempotencyKey = useIdempotencyKey();
  const mutation = useVaultTerminateMutation({ idempotencyKey, vaultId: vault.id });

  useEffect(() => {
    if (mutation.isSuccess) {
      navigate({ to: '/account/home', replace: true });
    }
  }, [mutation.isSuccess, navigate]);

  const handleCloseModal = useCallback(() => {
    onClose();
    mutation.reset();
  }, [onClose, mutation]);

  const isZero = isMoneyZero(vault.balance);

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleCloseModal}>
      <DialogTitle>
        {t(`Are you sure you want to close {{currency}} vault?`, {
          currency: vault.balance.currency,
        })}
      </DialogTitle>
      <DialogContent>
        <List>
          {isZero ? (
            <ListItem>
              <ListItemAvatar>
                <AvatarIcon icon={<TransactionIcon />} />
              </ListItemAvatar>
              <ListItemText
                primary={t('You can close the vault only when it has zero balance.')}
                secondary={t('To close this vault, please withdraw or transfer its balance first.')}
              />
            </ListItem>
          ) : (
            <ListItem>
              <ListItemAvatar>
                <AvatarIcon icon={<TransactionIcon />} />
              </ListItemAvatar>
              <ListItemText
                primary={t('The history of the Vault will be lost.')}
                secondary={t('You will be able to open a new Vault in the same currency any time.')}
              />
            </ListItem>
          )}
          {mutation.error ? <ErrorListItem error={mutation.error} /> : null}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal} color="primary" fullWidth>
          {t('Cancel')}
        </Button>
        {isZero ? (
          <Button
            onClick={() => mutation.mutate()}
            color="error"
            fullWidth
            loading={mutation.isPending}
          >
            {t('Close')}
          </Button>
        ) : (
          <ButtonLink
            to="/account/vaults/withdraw"
            color="secondary"
            search={{ vaultId: vault.id }}
            fullWidth
          >
            {t('Withdraw funds')}
          </ButtonLink>
        )}
      </DialogActions>
    </Dialog>
  );
}
